import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  FolderOpen,
  Gavel,
  KeyRound,
  Tag,
  Megaphone,
  HousePlus,
} from "lucide-react";
import { isLoggedIn } from "../../utils/isLoggedIn";
import {
  fetchMyListings,
  fetchUnlistedProperties,
} from "../../../pages/properties/listings/listingsApi";

// The "Properties" top-nav item: a dropdown instead of a plain link. My
// Properties (formerly a Survey Maps tab), Buy, Rent and Bank Auctions are the
// Listings page tabs, deep-linked with ?for=, and "Sell / List"
// opens the Listings list-a-property sheet via ?post=1. Clicking "Properties"
// itself opens My Properties and leaves this menu open.
export const PROPERTY_MENU = [
  { key: "mine", title: "My Properties", sub: "Your properties, documents and listing status", path: "/properties/listings?for=owned", Icon: FolderOpen },
  { key: "sell", title: "Buy", sub: "Homes, plots and land for sale", path: "/properties/listings?for=sell", Icon: Tag },
  { key: "rent", title: "Rent", sub: "Homes, shops and offices to rent", path: "/properties/listings?for=rent", Icon: KeyRound },
  { key: "list", title: "Sell / List your property", sub: "Post it for buyers or tenants", path: "/properties/listings?post=1", Icon: Megaphone },
  { key: "auction", title: "Bank Auctions", sub: "Bank e-auction properties", path: "/properties/listings?for=auction", Icon: Gavel },
];

// Shown instead of "My Properties" to a signed-in user who owns nothing yet:
// straight into the add-property form rather than an empty list.
const ADD_FIRST = {
  key: "mine",
  title: "Add property",
  sub: "Keep its documents and bills in one place",
  path: "/properties/listings?for=owned&add=1",
  Icon: HousePlus,
};

// Does the signed-in user own at least one property? One tiny request per pool
// (listed/unlisted + never-listed), asked once per session and remembered.
let ownsCache = null;
async function userOwnsProperty() {
  if (ownsCache !== null) return ownsCache;
  try {
    const [a, b] = await Promise.all([
      fetchMyListings({ pageSize: 1 }),
      fetchUnlistedProperties({ pageSize: 1 }),
    ]);
    ownsCache = a.items.length > 0 || b.items.length > 0;
  } catch {
    return true; // unknown → keep the normal "My Properties" entry
  }
  return ownsCache;
}

// Which menu row matches the current URL (highlighted while the menu is open).
// My Properties keeps ?for=owned in the URL (and its lockers live under
// /properties/my-properties); the other tabs strip ?for= once applied.
const currentKey = (pathname, search) =>
  pathname.startsWith("/properties/my-properties") ||
  (pathname.startsWith("/properties/listings") && search.includes("for=owned"))
    ? "mine"
    : null;

export default function PropertiesNavMenu({ active, onPick }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const btn = useRef(null);
  const timer = useRef(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const cur = currentKey(pathname, search);
  // null = not checked yet / logged out → the regular "My Properties" row.
  const [owns, setOwns] = useState(null);
  useEffect(() => {
    if (!open || !isLoggedIn()) return;
    let alive = true;
    userOwnsProperty().then((v) => alive && setOwns(v));
    return () => {
      alive = false;
    };
  }, [open]);
  // "Owns nothing" can change once they add one, so a "no" is re-asked on the
  // next open after visiting the Properties page; a "yes" stays.
  useEffect(() => {
    if (pathname.startsWith("/properties/listings") && ownsCache === false) ownsCache = null;
  }, [pathname, search]);
  const items = owns === false ? [ADD_FIRST, ...PROPERTY_MENU.slice(1)] : PROPERTY_MENU;

  // Close on an outside click / Escape, and whenever the route changes.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrap.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  // Set by a click on "Properties" itself, so the route change it causes
  // doesn't close the menu it just opened.
  const keepOpen = useRef(false);
  useEffect(() => {
    if (keepOpen.current) keepOpen.current = false;
    else setOpen(false);
  }, [pathname, search]);
  useEffect(() => () => clearTimeout(timer.current), []);

  // Hover opens it on a mouse; touch / pen rely on the click toggle.
  const fine = () => window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;
  const hover = (on) => {
    if (!fine()) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(on), on ? 200 : 250);
  };

  const pick = (it, stayOpen = false) => {
    if (!stayOpen) setOpen(false);
    window.scrollTo(0, 0);
    onPick?.(it);
    navigate(it.path);
  };

  const onMenuKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = [...wrap.current.querySelectorAll('[role="menuitem"]')];
    const i = items.indexOf(document.activeElement);
    const n = e.key === "ArrowDown" ? i + 1 : i - 1 + items.length;
    items[n % items.length]?.focus();
  };

  return (
    <div
      ref={wrap}
      className="relative"
      onMouseEnter={() => hover(true)}
      onMouseLeave={() => hover(false)}
      onKeyDown={onMenuKey}
    >
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          clearTimeout(timer.current);
          // Already on My Properties: just toggle the menu. Anywhere else: go
          // to My Properties with the menu open, or, for a signed-in user who
          // owns nothing yet, straight into the add-property flow.
          if (cur === "mine") return setOpen((v) => !v);
          // Asked here, not on the menu's open: the menu may not have been
          // opened yet this session, and then items[0] would always be
          // My Properties. Cached after the first ask.
          const check = isLoggedIn() ? userOwnsProperty() : Promise.resolve(null);
          check.then((v) => {
            setOwns(v);
            if (v === false) {
              // Owns nothing: start the add-property flow. Its sheet opens
              // over the page, so the menu stays closed.
              pick(ADD_FIRST);
              return;
            }
            keepOpen.current = true;
            setOpen(true);
            pick(PROPERTY_MENU[0], true);
          });
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            requestAnimationFrame(() => wrap.current?.querySelector('[role="menuitem"]')?.focus());
          }
        }}
        className={[
          "h-9 px-3.5 text-[15px] xl:h-10 xl:px-5 xl:text-[17px] whitespace-nowrap rounded-full transition-all duration-200 inline-flex items-center justify-center gap-1.5 align-middle",
          active || open
            ? "font-semibold bg-white text-[#153568] shadow-[0_6px_16px_-6px_rgba(0,0,0,0.35)]"
            : "font-medium text-white/90 hover:bg-white/[0.12] hover:text-white",
        ].join(" ")}
      >
        <Building2 size={17} strokeWidth={active ? 2.4 : 2} className="shrink-0" aria-hidden="true" />
        <span className="leading-none">Properties</span>
        <ChevronDown
          size={16}
          strokeWidth={2.4}
          aria-hidden="true"
          className={`-ml-0.5 shrink-0 opacity-85 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Properties"
          className="absolute left-1/2 top-[calc(100%+12px)] z-[1300] flex w-[340px] -translate-x-1/2 flex-col gap-0.5 rounded-2xl bg-white p-2 text-left whitespace-normal shadow-[0_24px_60px_-12px_rgba(8,21,43,0.45),0_0_0_1px_rgba(21,53,104,0.08)]"
        >
          {/* Hover bridge over the 12px gap, so the menu doesn't close on the way down. */}
          <span aria-hidden="true" className="absolute -top-3.5 left-0 right-0 h-3.5" />
          {/* Caret */}
          <span aria-hidden="true" className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 rounded-[2px] bg-white" />
          {items.map((it) => {
            const on = it.key === cur;
            const Icon = it.Icon;
            return (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                onClick={() => pick(it)}
                className={`relative flex w-full items-center gap-3 rounded-xl p-2.5 text-left text-[#153568] outline-none transition-colors hover:bg-[#f2f6fb] focus-visible:bg-[#f2f6fb] ${on ? "!bg-[#fff4dc]" : ""}`}
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${on ? "bg-[#f0a500] text-[#0c2244]" : "bg-[#e4edf8] text-[#153568]"}`}
                >
                  <Icon size={20} strokeWidth={2} aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <b className="block text-[14.5px] font-bold leading-snug">{it.title}</b>
                  <small className="mt-px block text-[12px] font-normal leading-snug text-[#5d6b7a]">{it.sub}</small>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

PropertiesNavMenu.propTypes = {
  active: PropTypes.bool,
  onPick: PropTypes.func,
};
